import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useQueries} from "react-query";
import axios from "axios";
import {useIntl, FormattedMessage} from "react-intl";
import {useLocation, useHistory} from "react-router-dom";
import {BotMessageSquare, Loader2, ArrowUpDown} from "lucide-react";
import {Skeleton} from "../../UIElements";
import {useSnackbar} from "../../Content";
import {useTriggerLLMAnalysis} from "../../api/APISession";
import {GetCategories, GetSupportNeeds, GetSurveys} from "../../api/APISurvey";
import {GetBots} from "../../api/APIBot";
import {getAuthConfig} from "../../utils";
import {getThemeColor} from "../../utils/topicUtils";
import {isBlocked} from "./StatsHeader";
import useSessionData from "./useSessionData";
import StatsHeader from "./StatsHeader";
import FilterBar, {DEFAULT_FILTERS, sessionMatchesFilters, sessionMatchesDateRange} from "./FilterBar";
import SessionRow from "./SessionRow";
import SessionDetailPanel from "./SessionDetailPanel";
import {sessionDate, parseTs, buildSupportNeedAttribution} from "./sessionUtils";
import {getCurrentQuarterRange, toDateString} from "./academicCalendar";
import "./ConversationAnalysis.scss";

const POLL_INTERVAL_MS = 4000

const auth = getAuthConfig()

function contactMatchesSearch(contact, query, contentSearch, topicLabels) {
    if (!query.trim()) return true
    const q = query.toLowerCase()

    if (contact.contactName?.toLowerCase().includes(q)) return true

    if (contentSearch) {
        for (const s of contact.sessions) {
            const a = s.llmanalysis
            if (!a) continue
            if (a.summary?.toLowerCase().includes(q)) return true
            if (a.success_evaluation_reason?.toLowerCase().includes(q)) return true
            if (a.provided_support?.toLowerCase().includes(q)) return true
            if (a.feedback_details?.toLowerCase().includes(q)) return true
            if (a.topics) {
                for (const code of a.topics) {
                    const label = topicLabels?.[code] || code
                    if (label.toLowerCase().includes(q)) return true
                }
            }
        }
    }

    return false
}

export default function ConversationAnalysis() {
    const intl = useIntl()
    const location = useLocation()
    const history = useHistory()
    const {setSnackbarData} = useSnackbar()
    const [filters, setFilters] = useState(() => {
        const qr = getCurrentQuarterRange()
        return {...DEFAULT_FILTERS, dateFrom: toDateString(qr.start), dateTo: toDateString(qr.end)}
    })
    const [pendingAnalysis, setPendingAnalysis] = useState(new Set())
    const [pollingSessionIds, setPollingSessionIds] = useState(new Set())
    const [searchQuery, setSearchQuery] = useState("")
    const [recentlyAnalyzedSessionIds, setRecentlyAnalyzedSessionIds] = useState(new Set())
    const [sortOrder, setSortOrder] = useState("newest") // "newest" | "oldest" | "messages"
    // Sessions completed since the last time polling fully drained. Lets us report a single
    // cumulative "analysis complete for N" instead of one toast per (arbitrary) poll round.
    const completedAccumRef = useRef(0)

    const searchParams = new URLSearchParams(location.search)
    const preFilterSurvey = searchParams.get("survey")
    const sessionParam = searchParams.get("session")

    useEffect(() => {
        if (preFilterSurvey) {
            setFilters(prev => ({...prev, campaigns: [preFilterSurvey]}))
        }
    }, [preFilterSurvey])

    useEffect(() => {
        document.title = intl.formatMessage({
            id: "botConversations",
            defaultMessage: "Bot Conversations",
        }) + " | Annie Admin"
    }, [intl])

    const {
        contacts,
        sessions,
        loadingPhase,
        totalSessions,
        hasMore,
        ensureLoadedThrough,
        ensureSessionLoaded,
        refreshSession,
        refreshAll,
        isRefreshing,
    } = useSessionData({preFilterSurvey})

    // When the selected date-from changes (or once the initial load finishes),
    // make sure sessions are loaded back to that boundary.
    useEffect(() => {
        if (!filters.dateFrom) return
        ensureLoadedThrough(new Date(filters.dateFrom))
    // loadingPhase in deps ensures this fires once the mount/background load settles
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.dateFrom, loadingPhase])

    const getCategoryData = GetCategories()
    const categoryData = getCategoryData.data?.[0] || {}

    const getSupportNeeds = GetSupportNeeds()
    const supportNeeds = useMemo(() => {
        const data = getSupportNeeds.data
        return Array.isArray(data) ? data : (data?.supportneeds || [])
    }, [getSupportNeeds.data])

    const supportNeedsInRange = useMemo(() => {
        const from = filters.dateFrom ? parseTs(filters.dateFrom + " 00:00:00+00") : -Infinity
        const to = filters.dateTo ? parseTs(filters.dateTo + " 23:59:59+00") : Infinity
        return supportNeeds.filter(sn => {
            const t = parseTs(sn.created)
            return !isNaN(t) && t >= from && t <= to
        })
    }, [supportNeeds, filters.dateFrom, filters.dateTo])

    const needAttribution = useMemo(
        () => buildSupportNeedAttribution(sessions, supportNeedsInRange),
        [sessions, supportNeedsInRange]
    )

    const triggerAnalysis = useTriggerLLMAnalysis()

    // The open session is derived from the URL (?session=<id>), making it shareable.
    // The session's own `contact` field supplies the contactId, so the URL only needs
    // the session id. `null` until the session is present in the loaded set.
    // URL params are strings; session ids may be numeric — compare as strings, but keep
    // the original-typed id in `selected` so downstream `=== session.id` checks still match.
    const selected = useMemo(() => {
        if (!sessionParam) return null
        const s = sessions.find(x => String(x.id) === sessionParam)
        return s ? {contactId: s.contact, sessionId: s.id} : null
    }, [sessionParam, sessions])

    // Deep-link fallback: a shared link may point at a conversation older than the
    // lazily-loaded window. Once the background load settles, fetch the missing session
    // (and its contact's thread) on demand. Guard so an unknown id is tried only once.
    const attemptedSessionFetch = useRef(new Set())
    useEffect(() => {
        if (!sessionParam) return
        if (loadingPhase !== "done") return
        if (sessions.some(s => String(s.id) === sessionParam)) return
        if (attemptedSessionFetch.current.has(sessionParam)) return
        attemptedSessionFetch.current.add(sessionParam)
        ensureSessionLoaded(sessionParam)
    }, [sessionParam, loadingPhase, sessions, ensureSessionLoaded])

    const selectedContact = useMemo(() => {
        if (!selected) return null
        return contacts.find(c => c.contactId === selected.contactId) || null
    }, [selected, contacts])

    const getSurveys = GetSurveys()
    const getArchivedSurveys = GetSurveys("ARCHIVED")
    const surveyConfigs = getSurveys.data
    const archivedSurveyConfigs = getArchivedSurveys.data

    // Survey ids carried by both list endpoints. Used to detect sessions whose survey
    // is absent from the lists (e.g. "24/7" surveys that survey.php doesn't return).
    const listedSurveyIds = useMemo(() => {
        const ids = new Set()
        const add = (list) => {
            if (!Array.isArray(list)) return
            list.forEach(s => ids.add(String(s.id)))
        }
        add(surveyConfigs)
        add(archivedSurveyConfigs)
        return ids
    }, [surveyConfigs, archivedSurveyConfigs])

    // Survey ids referenced by loaded sessions but missing from both lists. Gated on the
    // lists having settled so we never fetch a survey the list would have supplied.
    const missingSurveyIds = useMemo(() => {
        if (!getSurveys.isFetched || !getArchivedSurveys.isFetched) return []
        const set = new Set()
        sessions.forEach(s => {
            if (s.survey && !listedSurveyIds.has(String(s.survey))) set.add(s.survey)
        })
        return Array.from(set)
    }, [sessions, listedSurveyIds, getSurveys.isFetched, getArchivedSurveys.isFetched])

    // Fetch each missing survey individually via survey.php/{id}; React Query dedupes/caches.
    const missingSurveyQueries = useQueries(
        missingSurveyIds.map(id => ({
            queryKey: ["survey", id],
            queryFn: async () => {
                const {data} = await axios.get(`/api/v2/survey.php/${id}`, auth)
                return data
            },
            staleTime: 5 * 60 * 1000,
            enabled: !!id,
        }))
    )

    // survey.php/{id} returns an array; the survey object is at [0]. Unwrap to match the
    // {id, config} shape of the list endpoints.
    const extraSurveyConfigs = useMemo(
        () => missingSurveyQueries.map(q => q.data?.[0]).filter(Boolean),
        [missingSurveyQueries]
    )

    const surveyNames = useMemo(() => {
        const names = {}
        const addConfigs = (list) => {
            if (!Array.isArray(list)) return
            list.forEach(s => {
                if (names[s.id]) return
                const campaign = s.config?.campaign?.trim()
                const title = s.config?.title?.trim()
                names[s.id] = campaign || title || s.id
            })
        }
        addConfigs(surveyConfigs)
        addConfigs(archivedSurveyConfigs)
        addConfigs(extraSurveyConfigs)
        // Fallback for sessions whose survey isn't in the fetched lists or the per-id fetch
        sessions.forEach(s => {
            if (s.survey && !names[s.survey]) {
                names[s.survey] = s.survey
            }
        })
        return names
    }, [sessions, surveyConfigs, archivedSurveyConfigs, extraSurveyConfigs])

    // surveyId -> campaign grouping key. Surveys that share a `config.campaign` collapse to
    // one key (the campaign name) so the filter treats them as a single campaign; surveys
    // without a campaign stay distinct under their own id (labelled by title/id).
    const surveyCampaignKey = useMemo(() => {
        const keys = {}
        const addConfigs = (list) => {
            if (!Array.isArray(list)) return
            list.forEach(s => {
                if (keys[s.id]) return
                const campaign = s.config?.campaign?.trim()
                keys[s.id] = campaign || s.id
            })
        }
        addConfigs(surveyConfigs)
        addConfigs(archivedSurveyConfigs)
        addConfigs(extraSurveyConfigs)
        sessions.forEach(s => {
            if (s.survey && !keys[s.survey]) keys[s.survey] = s.survey
        })
        return keys
    }, [sessions, surveyConfigs, archivedSurveyConfigs, extraSurveyConfigs])

    // Support-bot modules. Sessions have no per-session module field; the source of truth for
    // which modules a conversation could involve is its campaign's config.supportBots.
    const getBots = GetBots()
    const botById = useMemo(() => {
        const map = {}
        const bots = Array.isArray(getBots.data) ? getBots.data : []
        bots.forEach(bot => {
            // Versions of the same module share a versionGroup — collapse them so the filter
            // shows one named entry, not one per version.
            const key = String(bot.config?.versionGroup || bot.id)
            map[bot.id] = {key, name: bot.config?.name || String(bot.id)}
        })
        return map
    }, [getBots.data])

    // surveyId -> [moduleKey, ...], derived from each survey's config.supportBots.
    const surveyModules = useMemo(() => {
        const map = {}
        const addConfigs = (list) => {
            if (!Array.isArray(list)) return
            list.forEach(s => {
                if (map[s.id]) return
                const botIds = Array.isArray(s.config?.supportBots) ? s.config.supportBots : []
                const keys = new Set()
                botIds.forEach(id => {
                    const entry = botById[id]
                    if (entry) keys.add(entry.key)
                })
                map[s.id] = Array.from(keys)
            })
        }
        addConfigs(surveyConfigs)
        addConfigs(archivedSurveyConfigs)
        addConfigs(extraSurveyConfigs)
        return map
    }, [surveyConfigs, archivedSurveyConfigs, extraSurveyConfigs, botById])

    // [{key, name}] for the filter list — only modules whose campaigns appear in loaded sessions.
    const moduleOptions = useMemo(() => {
        const presentSurveys = new Set(sessions.map(s => s.survey).filter(Boolean))
        const seenKeys = new Set()
        const byKeyName = new Map()
        Object.values(botById).forEach(({key, name}) => {
            if (!byKeyName.has(key)) byKeyName.set(key, name)
        })
        presentSurveys.forEach(surveyId => {
            (surveyModules[surveyId] || []).forEach(key => seenKeys.add(key))
        })
        return Array.from(seenKeys)
            .map(key => ({key, name: byKeyName.get(key) || key}))
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [sessions, surveyModules, botById])

    const topicLabels = useMemo(() => {
        const labels = {}
        sessions.forEach(s => {
            s.llmanalysis?.topics?.forEach(code => {
                if (!labels[code]) {
                    const msg = intl.formatMessage({id: `topic.${code}`, defaultMessage: code})
                    labels[code] = msg !== `topic.${code}` ? msg : code
                }
            })
        })
        return labels
    }, [sessions, intl])

    // One row per raw session, globally newest-first. Thread index/total are
    // computed over the contact's FULL thread so the position cue stays accurate
    // even when filters hide sibling sessions.
    const sessionRows = useMemo(() => {
        const rows = []
        for (const contact of contacts) {
            if (!contactMatchesSearch(contact, searchQuery, true, topicLabels)) continue
            const thread = [...contact.sessions].sort((a, b) => sessionDate(a).localeCompare(sessionDate(b)))
            thread.forEach((session, threadIndex) => {
                if (!sessionMatchesFilters(session, filters, supportNeeds, surveyModules, surveyCampaignKey, needAttribution)) return
                rows.push({session, contact, threadIndex, threadTotal: thread.length})
            })
        }

        rows.sort((a, b) => {
            if (sortOrder === "messages") {
                const am = Array.isArray(a.session.messages) ? a.session.messages.length : 0
                const bm = Array.isArray(b.session.messages) ? b.session.messages.length : 0
                if (bm !== am) return bm - am
                return sessionDate(b.session).localeCompare(sessionDate(a.session)) // tiebreak: newest
            }
            if (sortOrder === "oldest") return sessionDate(a.session).localeCompare(sessionDate(b.session))
            return sessionDate(b.session).localeCompare(sessionDate(a.session)) // newest (default)
        })

        // Temporarily pin sessions that just finished analysis to the top, preserving order within each group
        if (recentlyAnalyzedSessionIds.size === 0) return rows
        const pinned = rows.filter(r => recentlyAnalyzedSessionIds.has(r.session.id))
        const rest = rows.filter(r => !recentlyAnalyzedSessionIds.has(r.session.id))
        return [...pinned, ...rest]
    }, [contacts, filters, supportNeeds, surveyModules, surveyCampaignKey, needAttribution, searchQuery, topicLabels, recentlyAnalyzedSessionIds, sortOrder])

    const studentCount = useMemo(
        () => new Set(sessionRows.map(r => r.contact.contactId)).size,
        [sessionRows]
    )

    const filteredSessions = useMemo(() => {
        return sessions.filter(s => sessionMatchesFilters(s, filters, supportNeeds, surveyModules, surveyCampaignKey, needAttribution))
    }, [sessions, filters, supportNeeds, surveyModules, surveyCampaignKey, needAttribution])

    // Conversations within the selected date range, ignoring all other filters/search.
    // Lets the empty states tell "nothing in this period" from "filters excluded all".
    const periodSessionCount = useMemo(() => {
        return sessions.filter(s => sessionMatchesDateRange(s, filters)).length
    }, [sessions, filters])

    // Requests created in scope: needs attributed to a session that passes the filters.
    const filteredSupportNeedsCount = useMemo(() => {
        let count = 0
        filteredSessions.forEach(s => {
            count += needAttribution.get(s.id)?.length || 0
        })
        return count
    }, [filteredSessions, needAttribution])

    const escalationPendingCount = useMemo(() => {
        return filteredSessions.filter(s => {
            const a = s.llmanalysis
            if (!a || isBlocked(s) || a.support_request_suggested !== true) return false
            return !supportNeeds.some(sn => sn.contact === s.contact && sn.survey === s.survey)
        }).length
    }, [filteredSessions, supportNeeds])

    const topicThemeMap = useMemo(() => {
        const map = {}
        Object.entries(categoryData).forEach(([code, data]) => {
            if (data.theme) map[code] = data.theme
        })
        return map
    }, [categoryData])

    const themeGroupedCounts = useMemo(() => {
        const counts = {}
        filteredSessions.forEach(s => {
            if (s.llmanalysis && !isBlocked(s)) {
                s.llmanalysis.topics?.forEach(code => {
                    counts[code] = (counts[code] || 0) + 1
                })
            }
        })

        const themeMap = {}
        Object.entries(counts).forEach(([code, count]) => {
            const theme = topicThemeMap[code] || "99"
            if (!themeMap[theme]) {
                themeMap[theme] = {
                    theme,
                    themeLabel: intl.formatMessage({id: `theme.${theme}`, defaultMessage: theme}),
                    color: getThemeColor(theme),
                    totalCount: 0,
                    topics: [],
                }
            }
            themeMap[theme].totalCount += count
            themeMap[theme].topics.push({code, label: topicLabels[code] || code, count})
        })

        return Object.values(themeMap)
            .sort((a, b) => b.totalCount - a.totalCount)
            .map(t => ({...t, topics: t.topics.sort((a, b) => b.count - a.count)}))
    }, [filteredSessions, topicLabels, topicThemeMap, intl])

    const handleStatFilter = useCallback((filterKey) => {
        setFilters(prev => {
            if (filterKey === "notAnalyzed") {
                return {...prev, analyzedStatus: prev.analyzedStatus === "notAnalyzed" ? "all" : "notAnalyzed"}
            }
            if (filterKey === "blocked") {
                return {...prev, analyzedStatus: prev.analyzedStatus === "blocked" ? "all" : "blocked"}
            }
            if (filterKey === "successful") {
                return {...prev, successStatus: prev.successStatus === "successful" ? "all" : "successful"}
            }
            if (filterKey === "failed") {
                return {...prev, successStatus: prev.successStatus === "failed" ? "all" : "failed"}
            }
            if (filterKey === "uncertain") {
                return {...prev, successStatus: prev.successStatus === "uncertain" ? "all" : "uncertain"}
            }
            if (filterKey === "supportGiven") {
                return {...prev, supportGiven: prev.supportGiven === "yes" ? "all" : "yes"}
            }
            if (filterKey === "requestSuggested") {
                return {...prev, requestSuggested: prev.requestSuggested === "yes" ? "all" : "yes"}
            }
            if (filterKey === "needCreated") {
                return {...prev, needCreated: prev.needCreated === "yes" ? "all" : "yes"}
            }
            if (filterKey === "escalationPending") {
                return {...prev, escalationPending: prev.escalationPending === "yes" ? "all" : "yes"}
            }
            return prev
        })
    }, [])

    // Stat filters are independent dimensions that can be active simultaneously
    // (e.g. a Bot Performance filter + a Bot Actions filter), so track all of them.
    const activeStatFilters = useMemo(() => {
        const active = new Set()
        if (filters.analyzedStatus === "notAnalyzed") active.add("notAnalyzed")
        if (filters.analyzedStatus === "blocked") active.add("blocked")
        if (filters.successStatus === "successful") active.add("successful")
        if (filters.successStatus === "failed") active.add("failed")
        if (filters.successStatus === "uncertain") active.add("uncertain")
        if (filters.supportGiven === "yes") active.add("supportGiven")
        if (filters.requestSuggested === "yes") active.add("requestSuggested")
        if (filters.needCreated === "yes") active.add("needCreated")
        if (filters.escalationPending === "yes") active.add("escalationPending")
        return active
    }, [filters])

    const handleTopicClick = useCallback((code, themeTopicCodes) => {
        setFilters(prev => {
            if (themeTopicCodes) {
                const activeInTheme = themeTopicCodes.filter(c => prev.topics.includes(c))
                if (activeInTheme.length === 1 && activeInTheme[0] === code) {
                    return {...prev, topics: prev.topics.filter(t => t !== code)}
                }
                const withoutTheme = prev.topics.filter(t => !themeTopicCodes.includes(t))
                return {...prev, topics: [...withoutTheme, code]}
            }
            return {
                ...prev,
                topics: prev.topics.includes(code)
                    ? prev.topics.filter(t => t !== code)
                    : [...prev.topics, code]
            }
        })
    }, [])

    const handleThemeClick = useCallback((themeCode, topicCodes) => {
        setFilters(prev => {
            const allActive = topicCodes.every(c => prev.topics.includes(c))
            if (allActive) {
                return {...prev, topics: prev.topics.filter(t => !topicCodes.includes(t))}
            }
            const merged = new Set([...prev.topics, ...topicCodes])
            return {...prev, topics: Array.from(merged)}
        })
    }, [])

    const handleRequestSessionAnalysis = useCallback(async (sessionId) => {
        setPendingAnalysis(prev => new Set(prev).add(sessionId))

        try {
            await triggerAnalysis.mutateAsync({session: sessionId})
            setPollingSessionIds(prev => new Set(prev).add(sessionId))
        } catch (err) {
            console.error(`Analysis failed for session ${sessionId}:`, err)
            setPendingAnalysis(prev => {
                const next = new Set(prev)
                next.delete(sessionId)
                return next
            })
            setSnackbarData({
                show: true,
                text: intl.formatMessage({id: "analysis.analysisError", defaultMessage: "Couldn't start analysis. Please try again."}),
                state: "error",
                autoHide: true,
            })
        }
    }, [triggerAnalysis, setSnackbarData, intl])

    const handleBulkAnalysis = useCallback(async () => {
        const unanalyzedInScope = filteredSessions.filter(s => !s.llmanalysis)
        if (unanalyzedInScope.length === 0) return

        const sessionIds = unanalyzedInScope.map(s => s.id)
        setPendingAnalysis(prev => {
            const next = new Set(prev)
            sessionIds.forEach(id => next.add(id))
            return next
        })

        // Trigger in bounded-concurrency batches. This way results surface progressively
        const CONCURRENCY = 5
        const succeeded = []
        const failed = []
        for (let i = 0; i < sessionIds.length; i += CONCURRENCY) {
            const batch = sessionIds.slice(i, i + CONCURRENCY)
            const results = await Promise.all(batch.map(async (sessionId) => {
                try {
                    await triggerAnalysis.mutateAsync({session: sessionId})
                    return {sessionId, ok: true}
                } catch (err) {
                    console.error(`Bulk analysis failed for session ${sessionId}:`, err)
                    return {sessionId, ok: false}
                }
            }))

            const batchOk = results.filter(r => r.ok).map(r => r.sessionId)
            const batchFail = results.filter(r => !r.ok).map(r => r.sessionId)
            succeeded.push(...batchOk)
            failed.push(...batchFail)

            if (batchOk.length > 0) {
                setPollingSessionIds(prev => {
                    const next = new Set(prev)
                    batchOk.forEach(id => next.add(id))
                    return next
                })
            }

            if (batchFail.length > 0) {
                setPendingAnalysis(prev => {
                    const next = new Set(prev)
                    batchFail.forEach(id => next.delete(id))
                    return next
                })
            }
        }

        if (succeeded.length === 0) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({id: "analysis.analysisError", defaultMessage: "Couldn't start analysis. Please try again."}),
                state: "error",
                autoHide: true,
            })
        } else if (failed.length > 0) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage(
                    {id: "analysis.bulkAnalysisPartial", defaultMessage: "Analysis requested for {ok} of {total} conversations; {failed} couldn't be started"},
                    {ok: succeeded.length, total: sessionIds.length, failed: failed.length}
                ),
                state: "warning",
                autoHide: true,
            })
        } else {
            setSnackbarData({
                show: true,
                text: intl.formatMessage(
                    {id: "analysis.bulkAnalysisRequested", defaultMessage: "{count, plural, one {Analysis requested for # conversation} other {Analysis requested for # conversations}}"},
                    {count: succeeded.length}
                ),
                state: "info",
                autoHide: true,
            })
        }
    }, [filteredSessions, triggerAnalysis, setSnackbarData, intl])

    useEffect(() => {
        if (pollingSessionIds.size === 0) return

        // Self-scheduling poll: the next round is queued only after the current round's
        // requests resolve, so rounds never overlap/stack under bulk. `cancelled` keeps a
        // superseded or torn-down round from re-fetching removed sessions or double-firing.
        let cancelled = false
        let timer = null

        const poll = async () => {
            const completed = []
            for (const sessionId of pollingSessionIds) {
                if (cancelled) return
                const updated = await refreshSession(sessionId)
                if (updated?.llmanalysis) {
                    completed.push(sessionId)
                }
            }
            if (cancelled) return

            if (completed.length > 0) {
                setPendingAnalysis(prev => {
                    const next = new Set(prev)
                    completed.forEach(id => next.delete(id))
                    return next
                })

                // Pin the sessions that just finished analysis to the top
                setRecentlyAnalyzedSessionIds(prev => {
                    const next = new Set(prev)
                    completed.forEach(id => next.add(id))
                    return next
                })

                // Accumulate across rounds; only announce once the whole batch has finished.
                // `pollingSessionIds` is stable for the lifetime of this effect (a change to it
                // tears this round down via `cancelled`), so remaining is simply size - completed.
                completedAccumRef.current += completed.length
                const remaining = pollingSessionIds.size - completed.length

                setPollingSessionIds(prev => {
                    const next = new Set(prev)
                    completed.forEach(id => next.delete(id))
                    return next
                })

                if (remaining === 0) {
                    const total = completedAccumRef.current
                    completedAccumRef.current = 0
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {id: "analysis.analysisComplete", defaultMessage: "{count, plural, one {Analysis complete for # conversation} other {Analysis complete for # conversations}}"},
                            {count: total}
                        ),
                        state: "success",
                        autoHide: true,
                    })
                }
            }

            if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS)
        }

        timer = setTimeout(poll, POLL_INTERVAL_MS)

        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [pollingSessionIds, refreshSession, setSnackbarData, intl])

    const isLoading = loadingPhase === "initial" || loadingPhase === "background" || loadingPhase === "extending"
    const unanalyzedInScope = filteredSessions.filter(s => !s.llmanalysis).length
    const analyzedInScope = filteredSessions.filter(s => s.llmanalysis && !isBlocked(s)).length
    const analyzingRemaining = filteredSessions.filter(s => pendingAnalysis.has(s.id)).length

    const avgMessagesRaw = filteredSessions.length > 0
        ? filteredSessions.reduce((sum, s) => sum + (Array.isArray(s.messages) ? s.messages.length : 0), 0) / filteredSessions.length
        : 0
    const avgMessages = Number.isInteger(avgMessagesRaw) ? avgMessagesRaw : avgMessagesRaw.toFixed(1)

    // Write the ?session=<id> param (preserving other params like ?survey=) so the
    // open conversation is reflected in — and driven by — the URL.
    const setSessionParam = useCallback((sessionId, replace = false) => {
        const params = new URLSearchParams(location.search)
        if (sessionId) params.set("session", sessionId)
        else params.delete("session")
        const qs = params.toString()
        const to = `/conversations${qs ? "?" + qs : ""}`
        replace ? history.replace(to) : history.push(to)
    }, [history, location.search])

    // Toggle: clicking the focused row closes the panel; any other row (re)focuses.
    const handleSessionClick = useCallback((session) => {
        setSessionParam(sessionParam === String(session.id) ? null : session.id)
    }, [setSessionParam, sessionParam])

    const handleClosePanel = useCallback(() => {
        setSessionParam(null)
    }, [setSessionParam])

    // Promote a sibling session within the open panel to the focused (hero) one,
    // keeping the same student. `replace` so back-button history isn't flooded with
    // every in-panel focus change.
    const handleFocusSession = useCallback((sessionId) => {
        setSessionParam(sessionId, true)
    }, [setSessionParam])

    return <div className="content conversation-analysis">
        <div className="header-container">
            <h1>
                <BotMessageSquare size={24} strokeWidth={2} />
                {intl.formatMessage({
                    id: "botConversations",
                    defaultMessage: "Bot Conversations"
                })}
            </h1>
        </div>

        <div className="analysis-body">
            <div className="analysis-main">
                <FilterBar
                    filters={filters}
                    onFiltersChange={setFilters}
                    sessions={sessions}
                    surveyNames={surveyNames}
                    surveyCampaignKey={surveyCampaignKey}
                    moduleOptions={moduleOptions}
                    topicLabels={topicLabels}
                    topicThemeMap={topicThemeMap}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    onRefresh={() => {
                        setRecentlyAnalyzedSessionIds(new Set())
                        refreshAll(filters.dateFrom ? new Date(filters.dateFrom) : new Date())
                    }}
                    isRefreshing={isRefreshing}
                />
                {loadingPhase !== "idle" && (
                    <StatsHeader
                        sessions={filteredSessions}
                        supportNeedsCount={filteredSupportNeedsCount}
                        escalationPendingCount={escalationPendingCount}
                        activeFilter={activeStatFilters}
                        onFilterClick={handleStatFilter}
                        themeGroupedCounts={themeGroupedCounts}
                        activeTopics={filters.topics}
                        onTopicClick={handleTopicClick}
                        onThemeClick={handleThemeClick}
                        totalSessions={filteredSessions.length}
                        analyzedCount={analyzedInScope}
                        unanalyzedCount={unanalyzedInScope}
                        onAnalyzeAll={handleBulkAnalysis}
                        isAnalyzing={analyzingRemaining > 0}
                        analyzingRemaining={analyzingRemaining}
                        periodSessionCount={periodSessionCount}
                        isLoading={isLoading}
                    />
                )}

                <div className="toolbar-status">
                    <div className="status-side">
                        {recentlyAnalyzedSessionIds.size > 0 && (
                            <span className="recently-analyzed-label">
                                <FormattedMessage id="analysis.recentlyAnalyzed" defaultMessage="Recently analyzed" />
                            </span>
                        )}
                        <hr />
                    </div>

                    {loadingPhase === "initial" || loadingPhase === "background" || loadingPhase === "extending" ? (
                        <span className="status-text">
                            <Loader2 size={14} className="spin" />
                            {loadingPhase === "initial"
                                ? intl.formatMessage({id: "analysis.loading", defaultMessage: "Loading..."})
                                : intl.formatMessage(
                                    {id: "analysis.loadingProgress", defaultMessage: "Loading... {count} conversations"},
                                    {count: totalSessions}
                                )
                            }
                        </span>
                    ) : (
                        <span className="status-text">
                            <FormattedMessage
                                id="analysis.statusLine"
                                defaultMessage="{students, plural, one {# student} other {# students}} · {conversations, plural, one {# conversation} other {# conversations}} · {avg} avg {avg, plural, one {message} other {messages}}"
                                values={{students: studentCount, conversations: sessionRows.length, avg: avgMessages}}
                            />
                        </span>
                    )}

                    <div className="status-side">
                        <hr />
                        <label className="sort-select">
                            <ArrowUpDown size={14} />
                            <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
                                <option value="newest">{intl.formatMessage({id: "analysis.sortNewest", defaultMessage: "Newest first"})}</option>
                                <option value="oldest">{intl.formatMessage({id: "analysis.sortOldest", defaultMessage: "Oldest first"})}</option>
                                <option value="messages">{intl.formatMessage({id: "analysis.sortMessages", defaultMessage: "Most messages"})}</option>
                            </select>
                        </label>
                    </div>
                </div>

                {loadingPhase === "initial" && sessions.length === 0 ? (
                    <div className="contact-list">
                        <Skeleton width="100%" height="72px" />
                        <Skeleton width="100%" height="72px" />
                        <Skeleton width="100%" height="72px" />
                    </div>
                ) : (
                    <div className="contact-list">
                        {sessionRows.map(({session, contact, threadIndex, threadTotal}, index) => {
                            // Divider marks the boundary between the pinned recently-analyzed
                            const showDivider = recentlyAnalyzedSessionIds.size > 0 && index > 0 &&
                                !recentlyAnalyzedSessionIds.has(session.id) &&
                                recentlyAnalyzedSessionIds.has(sessionRows[index - 1].session.id)
                            return (
                                <React.Fragment key={session.id}>
                                    {showDivider && <hr className="recently-analyzed-divider" />}
                                    <SessionRow
                                        session={session}
                                        contact={contact}
                                        threadIndex={threadIndex}
                                        threadTotal={threadTotal}
                                        isSelected={selected?.sessionId === session.id}
                                        onClick={handleSessionClick}
                                        onRequestAnalysis={handleRequestSessionAnalysis}
                                        pendingAnalysis={pendingAnalysis.has(session.id)}
                                        surveyNames={surveyNames}
                                        topicLabels={topicLabels}
                                        topicThemes={topicThemeMap}
                                        supportNeeds={supportNeeds}
                                        needAttribution={needAttribution}
                                    />
                                </React.Fragment>
                            )
                        })}

                        {sessionRows.length === 0 && loadingPhase === "done" && (
                            <div className="no-results">
                                {periodSessionCount === 0 ? (
                                    <FormattedMessage id="analysis.noConversationsInPeriod" defaultMessage="No conversations in the selected period" />
                                ) : (
                                    <FormattedMessage id="analysis.noResults" defaultMessage="No conversations match the current filters" />
                                )}
                            </div>
                        )}
                    </div>
                )}

                {loadingPhase === "extending" && (
                    <div className="load-more-container">
                        <Loader2 size={20} className="spin" />
                    </div>
                )}
            </div>

            {selectedContact && <div className="detail-panel-overlay" onClick={handleClosePanel} />}

            {selectedContact && (
                <SessionDetailPanel
                    contact={selectedContact}
                    focusSessionId={selected?.sessionId}
                    onClose={handleClosePanel}
                    onFocusSession={handleFocusSession}
                    surveyNames={surveyNames}
                    topicLabels={topicLabels}
                    topicThemes={topicThemeMap}
                    supportNeeds={supportNeeds}
                    needAttribution={needAttribution}
                    onRequestAnalysis={handleRequestSessionAnalysis}
                    pendingAnalysisSessions={pendingAnalysis}
                />
            )}
        </div>
    </div>
}
